import {
  AssetType,
  ApplicationAssetType,
  CreateApplicationAssetMutationFn,
  CreateProjectAssetMutationFn,
  CreatePlanterRegistrationAssetMutationFn,
  CreatePlantingAssetMutationFn,
  CreateMonitoringAssetMutationFn,
  CreateAccountAssetMutationFn,
  AccountAssetType,
  ProjectAssetType,
  CreateProjectHeroMutationFn,
  CreatePlantingSiteAssetMutationFn,
  PlantingSiteAssetType,
  ProjectNoteAssetType,
  CreateProjectNoteAssetMutationFn
} from 'src/config/generated/graphql'
import { useRollback } from 'src/hooks/useRollback'
import { logError } from 'src/utils/errors'

export enum UploadAssetType {
  Application,
  Avatar,
  Banner,
  Monitoring,
  PlanterRegistration,
  Planting,
  Project,
  ProjectBanner,
  PlantingSite,
  ProjectNote
}

type AssetMutationFn =
  | CreateApplicationAssetMutationFn
  | CreateProjectAssetMutationFn
  | CreatePlanterRegistrationAssetMutationFn
  | CreatePlantingAssetMutationFn
  | CreateAccountAssetMutationFn
  | CreateMonitoringAssetMutationFn
  | CreateProjectHeroMutationFn
  | CreatePlantingSiteAssetMutationFn
  | CreateProjectNoteAssetMutationFn

type FileToUploadType =
  | AssetType
  | ApplicationAssetType
  | AccountAssetType
  | ProjectAssetType
  | PlantingSiteAssetType
  | ProjectNoteAssetType

type AssetMutationResult = {
  data?: { asset: { SignedUrl: string; Id: string } }
}

export type FileToUpload = {
  file: File
  type: FileToUploadType
}

const PRIMARY_KEY_FIELD_MAPPING = {
  [UploadAssetType.Application]: 'ApplicationId',
  [UploadAssetType.Monitoring]: 'MonitoringEventId',
  [UploadAssetType.Planting]: 'PlantingId',
  [UploadAssetType.Avatar]: 'AccountId',
  [UploadAssetType.Banner]: 'AccountId',
  [UploadAssetType.Project]: 'ProjectId',
  [UploadAssetType.ProjectBanner]: 'ProjectId',
  [UploadAssetType.PlanterRegistration]: 'PlanterRegistrationId',
  [UploadAssetType.PlantingSite]: 'PlantingSiteId',
  [UploadAssetType.ProjectNote]: 'ProjectNoteId'
}

const xhrUpload = async (
  url: string,
  file: File,
  onProgress?: (key: string, loaded: number, total: number) => void
): Promise<Response> => {
  return new Promise((success, failure) => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return
      }

      const response = new Response(xhr.response, {
        status: xhr.status,
        statusText: xhr.statusText
      })

      success(response)
    }

    xhr.addEventListener('error', () => {
      failure(new TypeError('Failed to fetch'))
    })

    if (onProgress) {
      xhr.upload.onprogress = v => {
        onProgress(file.name, v.loaded, v.total)
      }
    }

    xhr.open('PUT', url, true)
    xhr.send(file)
  })
}

const putObject = async (
  presignedPostUrl: AssetMutationResult,
  fileToUpload: FileToUpload,
  onProgress?: (key: string, loaded: number, total: number) => void
) => {
  if (presignedPostUrl.data?.asset.SignedUrl) {
    const url = presignedPostUrl.data?.asset.SignedUrl!

    const response = await xhrUpload(url, fileToUpload.file, onProgress)

    /* const response = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      body: fileToUpload.file
    }) */
    if (response.status !== 200) {
      throw new Error('File upload has failed')
    } else return response.body
  }
  throw new Error('No presignedPostUrl, could not upload file.')
}

export async function uploadFileToS3(
  createAsset: AssetMutationFn,
  fileToUpload: FileToUpload,
  assetType: UploadAssetType,
  modelId: string,
  rollbackAsset?: ReturnType<typeof useRollback>,
  onProgress?: (key: string, loaded: number, total: number) => void
) {
  const typedCreateAsset = createAsset as (options: {
    variables: { input: any }
  }) => Promise<any>

  const input: any = {
    ContentType: fileToUpload.file.type,
    FileName: fileToUpload.file.name,
    Type: fileToUpload.type as FileToUploadType,
    [PRIMARY_KEY_FIELD_MAPPING[assetType]]: modelId
  }

  const createResult: AssetMutationResult = await typedCreateAsset({
    variables: { input }
  })
  try {
    await putObject(
      createResult as AssetMutationResult & { [modelId]: string },
      fileToUpload,
      onProgress
    )
  } catch (err) {
    try {
      if (rollbackAsset && createResult.data?.asset.Id) {
        logError(new Error('Fileupload: Error uploading file'), input)
        await rollbackAsset(createResult.data.asset.Id)
      }
    } catch (e) {
      logError(new Error('Error rolling back file upload'), { error: e, input })
      // swallow error if rollback failed
    }
    throw err
  }
}

export async function uploadFilesToS3(
  createAsset: AssetMutationFn,
  filesToUpload: FileToUpload[],
  assetType: UploadAssetType,
  modelId: string,
  rollbackAsset?: ReturnType<typeof useRollback>,
  onProgress?: (key: string, loaded: number, total: number) => void
) {
  return Promise.all(
    filesToUpload.map((fileToUpload: FileToUpload) =>
      uploadFileToS3(
        createAsset,
        fileToUpload,
        assetType,
        modelId,
        rollbackAsset,
        onProgress
      )
    )
  )
}
